import { Injectable, signal } from '@angular/core';
import type { StaffModel } from '../models/auth.model';

/**
 * Manages the authenticated staff member's state throughout the application.
 * Stores the current staff information in an Angular signal and exposes
 * a read-only version of that signal for components and services that
 * need to access the authenticated staff member.
 */
@Injectable({ providedIn: 'root' })
export class StaffStateService {
  /**
   * Internal signal containing the currently authenticated staff member.
   * The value is `null` when no staff member is currently authenticated.
   */
  private readonly staffSignal = signal<StaffModel | null>(null);

  /**
   * Read-only signal containing the current staff member.
   * Consumers can read the staff state but cannot directly modify
   * the underlying signal.
   */
  readonly staff = this.staffSignal.asReadonly();

  /**
   * Updates the current authenticated staff member.
   * @param staff Staff member information to store in the application state.
   */
  setStaff(staff: StaffModel): void {
    this.staffSignal.set(staff);
  }

  /**
   * Clears the current staff member from the application state.
   * Sets the staff state back to `null`, indicating that no
   * authenticated staff member is currently stored.
   */
  clearStaff(): void {
    this.staffSignal.set(null);
  }
}